import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { AnimatedNumber } from '../hooks/useCountUp';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import OnboardingChecklist from '../components/OnboardingChecklist';
import {
  Users, ShieldCheck, Key, Cube, PaperPlaneTilt, CheckSquare, Clock, Pulse,
  GearSix, ArrowCounterClockwise,
  Ticket, Warning, ClipboardText, CalendarX, SpinnerGap, Check,
  SquaresFour, Rows, ListBullets, CaretRight,
  TrendUp, TrendDown, Minus, Lightning, ArrowRight,
  ShieldWarning, UserMinus, Play, Plus
} from '@phosphor-icons/react';

const WIDGET_DEFINITIONS = {
  identity_count: { label: 'Identities', icon: Users, color: 'text-navy-800', link: '/identities' },
  active_identities: { label: 'Active', icon: Users, color: 'text-emerald-700', link: '/identities' },
  role_count: { label: 'Roles', icon: ShieldCheck, color: 'text-purple-700', link: '/roles' },
  entitlement_count: { label: 'Entitlements', icon: Key, color: 'text-amber-700', link: '/entitlements' },
  app_count: { label: 'Applications', icon: Cube, color: 'text-blue-700', link: '/applications' },
  pending_requests: { label: 'Pending Requests', icon: PaperPlaneTilt, color: 'text-orange-700', accent: true, link: '/access-requests' },
  pending_approvals: { label: 'Pending Approvals', icon: CheckSquare, color: 'text-orange-700', accent: true, link: '/approvals' },
  active_assignments: { label: 'Assignments', icon: ShieldCheck, color: 'text-indigo-700', link: '/roles' },
  pending_tickets: { label: 'Tickets', icon: Ticket, color: 'text-rose-700', link: '/provisioning-tickets' },
  expiring_soon: { label: 'Expiring Soon', icon: CalendarX, color: 'text-amber-700', link: '/roles' },
  sod_violations: { label: 'SoD Violations', icon: Warning, color: 'text-red-700', link: '/sod' },
  active_certifications: { label: 'Certifications', icon: ClipboardText, color: 'text-cyan-700', link: '/certifications' },
};

const LAYOUT_OPTIONS = [
  { id: 'grid', label: 'Grid', icon: SquaresFour },
  { id: 'compact', label: 'Compact', icon: Rows },
  { id: 'detailed', label: 'Detailed', icon: ListBullets },
];

// Trend sparkline bar
function TrendBar({ label, icon: Icon, thisWeek, lastWeek, color }) {
  const diff = thisWeek - lastWeek;
  const pctChange = lastWeek > 0 ? Math.round((diff / lastWeek) * 100) : (thisWeek > 0 ? 100 : 0);
  const isUp = diff > 0;
  const isDown = diff < 0;
  const TrendIcon = isUp ? TrendUp : isDown ? TrendDown : Minus;

  return (
    <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden hover:shadow-md transition-shadow" data-testid={`trend-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className={`h-1.5 ${color.replace('text-', 'bg-').split(' ')[0]}`} />
      <div className="p-3 sm:p-4 flex items-center gap-3 sm:gap-4">
      <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center shrink-0 ${color}`}>
        <Icon size={18} weight="duotone" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] sm:text-xs text-navy-500 font-semibold uppercase tracking-widest">{label}</p>
        <div className="flex items-baseline gap-1.5 mt-0.5">
          <span className="text-lg sm:text-xl font-bold text-navy-900"><AnimatedNumber value={thisWeek} /></span>
          <span className="text-[10px] text-navy-400 hidden sm:inline">this week</span>
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className={`flex items-center gap-1 text-xs font-semibold ${isUp ? 'text-emerald-600' : isDown ? 'text-red-500' : 'text-navy-400'}`}>
          <TrendIcon size={14} weight="bold" />
          {Math.abs(pctChange)}%
        </div>
        <p className="text-[10px] text-navy-400 mt-0.5 hidden sm:block">vs {lastWeek}</p>
      </div>
      </div>
    </div>
  );
}

// Risk score gauge
function RiskGauge({ score, factors }) {
  const level = score >= 60 ? 'Critical' : score >= 30 ? 'Moderate' : 'Low';
  const levelColor = score >= 60 ? 'text-red-600' : score >= 30 ? 'text-amber-600' : 'text-emerald-600';
  const barColor = 'bg-orange-500';
  const bgColor = score >= 60 ? 'bg-orange-50' : score >= 30 ? 'bg-orange-50' : 'bg-navy-50';

  const factorItems = [
    { label: 'SoD Violations', value: factors?.sod_violations || 0, icon: Warning, bad: (factors?.sod_violations || 0) > 0 },
    { label: 'Orphaned Identities', value: factors?.orphaned_identities || 0, icon: UserMinus, bad: (factors?.orphaned_identities || 0) > 10 },
    { label: 'Expiring Access', value: factors?.expiring_access || 0, icon: CalendarX, bad: (factors?.expiring_access || 0) > 5 },
    { label: 'Pending Approvals', value: factors?.pending_approvals || 0, icon: CheckSquare, bad: (factors?.pending_approvals || 0) > 20 },
  ];

  return (
    <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden" data-testid="risk-score-widget">
      <div className={`h-1.5 ${barColor}`} />
      <div className="p-5">
      <div className="flex items-center gap-2 mb-4">
        <ShieldWarning size={18} weight="duotone" className="text-navy-700" />
        <h3 className="text-sm font-bold text-navy-900 uppercase tracking-widest">Risk Score</h3>
      </div>

      <div className="flex items-center gap-4 mb-4">
        <div className={`w-16 h-16 rounded-full ${bgColor} flex items-center justify-center`}>
          <span className={`text-2xl font-black ${levelColor}`}><AnimatedNumber value={score} duration={800} /></span>
        </div>
        <div>
          <p className={`text-sm font-bold ${levelColor}`}>{level} Risk</p>
          <p className="text-xs text-navy-400 mt-0.5">Based on {factorItems.filter(f => f.bad).length} active risk factors</p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full h-2 bg-slate-100 rounded-full mb-4">
        <div className={`h-2 rounded-full transition-all duration-500 ${barColor}`} style={{ width: `${Math.min(score, 100)}%` }} />
      </div>

      {/* Factor breakdown */}
      <div className="space-y-2">
        {factorItems.map(f => (
          <div key={f.label} className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-2">
              <f.icon size={12} className={f.bad ? 'text-red-500' : 'text-navy-400'} />
              <span className="text-navy-600">{f.label}</span>
            </div>
            <span className={`font-semibold ${f.bad ? 'text-red-600' : 'text-navy-500'}`}>{f.value}</span>
          </div>
        ))}
      </div>
      </div>
    </div>
  );
}

// Quick action buttons
function QuickActions({ navigate }) {
  const actions = [
    { label: 'Run Aggregation', icon: Play, path: '/admin', color: 'bg-navy-50 text-navy-700 hover:bg-navy-100' },
    { label: 'Check Policies', icon: Warning, path: '/sod', color: 'bg-orange-50 text-orange-700 hover:bg-orange-100' },
    { label: 'Create Campaign', icon: Plus, path: '/certifications', color: 'bg-navy-50 text-navy-700 hover:bg-navy-100' },
    { label: 'New Role', icon: ShieldCheck, path: '/roles', color: 'bg-orange-50 text-orange-700 hover:bg-orange-100' },
  ];

  return (
    <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden" data-testid="quick-actions-widget">
      <div className="h-1.5 bg-orange-500" />
      <div className="p-5">
      <div className="flex items-center gap-2 mb-4">
        <Lightning size={18} weight="duotone" className="text-orange-500" />
        <h3 className="text-xs font-bold text-navy-900 uppercase tracking-widest">Quick Actions</h3>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {actions.map(a => (
          <button
            key={a.label}
            data-testid={`quick-action-${a.label.toLowerCase().replace(/\s/g, '-')}`}
            onClick={() => navigate(a.path)}
            className={`flex items-center gap-2 px-3 py-2.5 rounded-sm text-xs font-semibold transition-colors ${a.color}`}
          >
            <a.icon size={16} weight="duotone" />
            {a.label}
          </button>
        ))}
      </div>
      </div>
    </div>
  );
}

// Enhanced activity item
function ActivityItem({ log, index }) {
  const actionColors = {
    create: 'bg-emerald-100 text-emerald-700',
    update: 'bg-blue-100 text-blue-700',
    delete: 'bg-red-100 text-red-700',
    approve: 'bg-emerald-100 text-emerald-700',
    reject: 'bg-red-100 text-red-700',
    bulk_fix: 'bg-amber-100 text-amber-700',
    jit_request: 'bg-purple-100 text-purple-700',
    jit_approve: 'bg-emerald-100 text-emerald-700',
  };

  const timeAgo = (ts) => {
    if (!ts) return '';
    const diff = Date.now() - new Date(ts).getTime();
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (mins > 0) return `${mins}m ago`;
    return 'just now';
  };

  return (
    <div className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors" data-testid={`activity-item-${index}`}>
      <div className="w-8 h-8 rounded-full bg-navy-100 flex items-center justify-center text-[10px] font-bold text-navy-600 shrink-0">
        {log.user_name?.charAt(0) || '?'}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-navy-700 truncate">
          <span className="font-semibold">{log.user_name}</span>
          {' '}<span className="text-navy-500">{log.action.replace(/_/g, ' ')}</span>{' '}
          <span className="font-medium">{log.entity_name}</span>
        </p>
        <p className="text-[10px] text-navy-400 mt-0.5">{log.entity_type} &middot; {timeAgo(log.timestamp)}</p>
      </div>
      <Badge className={`text-[9px] uppercase shrink-0 ${actionColors[log.action] || 'bg-slate-100 text-navy-600'}`}>
        {log.action.replace(/_/g, ' ')}
      </Badge>
    </div>
  );
}

// Stat card
function StatCard({ widgetKey, value, layout, onClick, index }) {
  const widget = WIDGET_DEFINITIONS[widgetKey];
  if (!widget) return null;
  const Icon = widget.icon;
  const isCompact = layout === 'compact';

  const TOP_BAR_COLORS = {
    identity_count: 'bg-navy-600', active_identities: 'bg-navy-600', role_count: 'bg-navy-600',
    entitlement_count: 'bg-navy-600', app_count: 'bg-navy-600', pending_requests: 'bg-orange-500',
    pending_approvals: 'bg-orange-500', active_assignments: 'bg-navy-600', pending_tickets: 'bg-orange-500',
    expiring_soon: 'bg-orange-500', sod_violations: 'bg-orange-500', active_certifications: 'bg-navy-600',
  };

  return (
    <div
      data-testid={`stat-${widgetKey}`}
      onClick={onClick}
      className={`bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-pointer group`}
      style={{ animationDelay: `${(index || 0) * 0.04}s` }}
    >
      <div className={`h-1.5 ${TOP_BAR_COLORS[widgetKey] || 'bg-slate-400'}`} />
      <div className={isCompact ? 'p-3' : 'p-4'}>
        <div className={`flex ${isCompact ? 'items-center gap-2' : 'items-start gap-3'}`}>
        <div className={`shrink-0 transition-transform duration-200 group-hover:scale-110 ${widget.color}`}>
          <Icon size={isCompact ? 18 : 22} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`font-bold text-navy-900 ${isCompact ? 'text-lg' : 'text-xl'}`}><AnimatedNumber value={value} /></p>
          <p className={`text-navy-500 font-semibold uppercase tracking-widest ${isCompact ? 'text-[8px]' : 'text-[10px]'}`}>{widget.label}</p>
        </div>
        <CaretRight size={12} className="text-navy-300 group-hover:text-orange-500 transition-colors shrink-0" />
      </div>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [preferences, setPreferences] = useState(null);
  const [riskIdentities, setRiskIdentities] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [tempPrefs, setTempPrefs] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [statsRes, prefsRes, riskRes] = await Promise.all([
        API.get('/dashboard/stats'),
        API.get('/dashboard/preferences'),
        API.get('/identities/risk-scores', { params: { limit: 10, min_score: 15 } }),
      ]);
      setStats(statsRes.data);
      setPreferences(prefsRes.data);
      setRiskIdentities(riskRes.data);
    } catch (err) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const openCustomize = () => { setTempPrefs({ ...preferences }); setShowCustomize(true); };

  const handleWidgetToggle = (widgetKey, enabled) => {
    setTempPrefs(prev => {
      const visible = [...(prev.visible_widgets || [])];
      const order = [...(prev.widget_order || [])];
      if (enabled) {
        if (!visible.includes(widgetKey)) visible.push(widgetKey);
        if (!order.includes(widgetKey)) order.push(widgetKey);
      } else {
        const idx = visible.indexOf(widgetKey);
        if (idx > -1) visible.splice(idx, 1);
      }
      return { ...prev, visible_widgets: visible, widget_order: order };
    });
  };

  const savePreferences = async () => {
    setSaving(true);
    try {
      await API.put('/dashboard/preferences', tempPrefs);
      setPreferences(tempPrefs);
      setShowCustomize(false);
      toast.success('Dashboard preferences saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const resetPreferences = async () => {
    setSaving(true);
    try {
      const res = await API.post('/dashboard/preferences/reset');
      setPreferences(res.data);
      setTempPrefs(res.data);
      toast.success('Dashboard reset to defaults');
    } catch { toast.error('Failed to reset'); }
    finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <SpinnerGap size={32} className="animate-spin text-orange-500" />
      </div>
    );
  }

  const s = stats || {};
  const prefs = preferences || { visible_widgets: Object.keys(WIDGET_DEFINITIONS), layout: 'grid', show_recent_activity: true };
  const visibleWidgets = prefs.visible_widgets || Object.keys(WIDGET_DEFINITIONS);
  const orderedWidgets = (prefs.widget_order || visibleWidgets).filter(w => visibleWidgets.includes(w));
  const gridCols = { grid: 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4', compact: 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4', detailed: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' };
  const trends = s.trends || {};

  return (
    <div data-testid="dashboard-page" className="p-4 sm:p-6 md:p-8 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy-900">Dashboard</h1>
          <p className="text-sm text-navy-500 mt-0.5">Identity Access Governance overview</p>
        </div>
        <Button variant="outline" onClick={openCustomize} className="rounded-sm text-sm" data-testid="customize-btn">
          <GearSix size={14} className="mr-1.5" /> Customize
        </Button>
      </div>

      {/* Onboarding Checklist */}
      <OnboardingChecklist />

      {/* Trend Sparklines Row */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3" data-testid="trends-row">
        <TrendBar label="Requests" icon={PaperPlaneTilt} thisWeek={trends.requests?.this_week || 0} lastWeek={trends.requests?.last_week || 0} color="bg-orange-100 text-orange-600" />
        <TrendBar label="Approvals" icon={CheckSquare} thisWeek={trends.approvals?.this_week || 0} lastWeek={trends.approvals?.last_week || 0} color="bg-orange-100 text-orange-600" />
        <TrendBar label="Assignments" icon={ShieldCheck} thisWeek={trends.assignments?.this_week || 0} lastWeek={trends.assignments?.last_week || 0} color="bg-navy-100 text-navy-600" />
        <TrendBar label="JIT Sessions" icon={Lightning} thisWeek={trends.jit?.this_week || 0} lastWeek={trends.jit?.last_week || 0} color="bg-navy-100 text-navy-600" />
      </div>

      {/* Middle Row: Risk Score + Quick Actions + Key Metrics */}
      <div className="grid lg:grid-cols-2 xl:grid-cols-12 gap-4">
        {/* Risk Score */}
        <div className="xl:col-span-3">
          <RiskGauge score={s.risk_score || 0} factors={s.risk_factors} />
        </div>

        {/* Quick Actions */}
        <div className="xl:col-span-3">
          <QuickActions navigate={navigate} />
        </div>

        {/* Key Stats Grid */}
        <div className="lg:col-span-2 xl:col-span-6">
          <div className={`grid gap-3 ${gridCols[prefs.layout] || gridCols.grid}`}>
            {orderedWidgets.slice(0, 12).map((widgetKey, index) => (
              <StatCard
                key={widgetKey}
                widgetKey={widgetKey}
                value={s[widgetKey] || 0}
                layout={prefs.layout || 'compact'}
                index={index}
                onClick={() => {
                  const widget = WIDGET_DEFINITIONS[widgetKey];
                  if (widget?.link) navigate(widget.link);
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Recent Activity Feed */}
      {prefs.show_recent_activity !== false && (
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden" data-testid="activity-feed">
          <div className="h-1.5 bg-orange-400" />
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Pulse size={16} weight="duotone" className="text-orange-500" />
              <h3 className="text-xs font-bold text-navy-900 uppercase tracking-widest">Recent Activity</h3>
            </div>
            <Button variant="ghost" size="sm" className="text-xs text-navy-500 h-7" onClick={() => navigate('/admin')}>
              View All <ArrowRight size={12} className="ml-1" />
            </Button>
          </div>
          <div className="divide-y divide-slate-100">
            {(s.recent_activity || []).length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-navy-400">No recent activity yet</div>
            ) : (
              (s.recent_activity || []).slice(0, 10).map((log, idx) => (
                <ActivityItem key={log.id || idx} log={log} index={idx} />
              ))
            )}
          </div>
        </div>
      )}

      {/* High-Risk Identities */}
      {riskIdentities && riskIdentities.identities?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden" data-testid="risk-identities-widget">
          <div className="h-1.5 bg-orange-500" />
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ShieldWarning size={16} weight="duotone" className="text-orange-500" />
              <h3 className="text-xs font-bold text-navy-900 uppercase tracking-widest">High-Risk Identities</h3>
              <Badge className="bg-red-100 text-red-700 text-[10px]">{riskIdentities.total}</Badge>
            </div>
            <div className="flex items-center gap-3 text-[10px]">
              {Object.entries(riskIdentities.distribution || {}).filter(([,v]) => v > 0).map(([level, count]) => (
                <span key={level} className={`flex items-center gap-1 font-semibold ${
                  level === 'critical' ? 'text-red-600' : level === 'high' ? 'text-orange-600' : level === 'medium' ? 'text-amber-600' : 'text-navy-400'
                }`}>
                  <span className={`w-2 h-2 rounded-full ${
                    level === 'critical' ? 'bg-red-500' : level === 'high' ? 'bg-orange-500' : level === 'medium' ? 'bg-amber-400' : 'bg-navy-300'
                  }`} />
                  {count} {level}
                </span>
              ))}
            </div>
          </div>
          <div className="divide-y divide-slate-100">
            {riskIdentities.identities.slice(0, 8).map((identity, idx) => {
              const scoreColor = identity.risk_level === 'critical' ? 'text-red-600 bg-red-50' :
                identity.risk_level === 'high' ? 'text-orange-600 bg-orange-50' :
                identity.risk_level === 'medium' ? 'text-amber-600 bg-amber-50' : 'text-navy-500 bg-slate-50';
              const f = identity.factors || {};
              return (
                <div key={identity.identity_id} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors cursor-pointer" 
                     onClick={() => navigate('/identities')}
                     data-testid={`risk-identity-${idx}`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${scoreColor}`}>
                    {identity.risk_score}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-navy-900 truncate">{identity.display_name}</p>
                    <p className="text-[10px] text-navy-400 truncate">{identity.department || 'No department'} &middot; {identity.email || 'No email'}</p>
                  </div>
                  <div className="flex gap-1 flex-wrap justify-end shrink-0 max-w-[220px]">
                    {f.privileged_roles > 0 && (
                      <Badge className="text-[9px] bg-purple-100 text-purple-700">{f.privileged_roles} priv</Badge>
                    )}
                    {f.sod_violations > 0 && (
                      <Badge className="text-[9px] bg-red-100 text-red-700">{f.sod_violations} SoD</Badge>
                    )}
                    {f.orphaned && (
                      <Badge className="text-[9px] bg-amber-100 text-amber-700">Orphaned</Badge>
                    )}
                    {f.active_jit_sessions > 0 && (
                      <Badge className="text-[9px] bg-emerald-100 text-emerald-700">{f.active_jit_sessions} JIT</Badge>
                    )}
                    {f.days_since_certification === null && (
                      <Badge className="text-[9px] bg-slate-100 text-navy-600">Uncertified</Badge>
                    )}
                    {f.days_since_certification > 90 && (
                      <Badge className="text-[9px] bg-orange-100 text-orange-700">Cert {f.days_since_certification}d ago</Badge>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Customize Dialog */}
      <Dialog open={showCustomize} onOpenChange={setShowCustomize}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GearSix size={20} weight="duotone" className="text-orange-500" />
              Customize Dashboard
            </DialogTitle>
          </DialogHeader>

          {tempPrefs && (
            <div className="space-y-6 pt-2">
              <div>
                <p className="text-sm font-semibold text-navy-900 mb-3">Layout</p>
                <div className="grid grid-cols-3 gap-3">
                  {LAYOUT_OPTIONS.map(opt => {
                    const Icon = opt.icon;
                    const sel = tempPrefs.layout === opt.id;
                    return (
                      <button key={opt.id} onClick={() => setTempPrefs(p => ({...p, layout: opt.id}))} data-testid={`layout-${opt.id}`}
                        className={`p-3 rounded-lg border-2 transition-all text-center ${sel ? 'border-orange-500 bg-orange-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}>
                        <Icon size={20} weight={sel ? 'duotone' : 'regular'} className={`mx-auto ${sel ? 'text-orange-500' : 'text-navy-400'}`} />
                        <p className={`text-xs font-semibold mt-1 ${sel ? 'text-orange-700' : 'text-navy-700'}`}>{opt.label}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-navy-900">Visible Widgets</p>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setTempPrefs(p => ({...p, visible_widgets: Object.keys(WIDGET_DEFINITIONS), widget_order: Object.keys(WIDGET_DEFINITIONS)}))} className="text-xs h-7 px-2">Show All</Button>
                    <Button variant="ghost" size="sm" onClick={() => setTempPrefs(p => ({...p, visible_widgets: []}))} className="text-xs h-7 px-2">Hide All</Button>
                  </div>
                </div>
                <div className="bg-slate-50 rounded-lg border border-slate-200 p-4 max-h-64 overflow-y-auto">
                  {Object.keys(WIDGET_DEFINITIONS).map(wk => {
                    const w = WIDGET_DEFINITIONS[wk];
                    const Icon = w.icon;
                    return (
                      <div key={wk} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                        <div className="flex items-center gap-3">
                          <Icon size={16} weight="duotone" className={w.color} />
                          <span className="text-sm text-navy-900">{w.label}</span>
                        </div>
                        <Switch checked={(tempPrefs.visible_widgets || []).includes(wk)} onCheckedChange={(v) => handleWidgetToggle(wk, v)} data-testid={`widget-toggle-${wk}`} />
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex items-center justify-between py-3 px-4 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-center gap-3">
                  <Pulse size={18} weight="duotone" className="text-orange-500" />
                  <span className="text-sm text-navy-900">Recent Activity Feed</span>
                </div>
                <Switch checked={tempPrefs.show_recent_activity} onCheckedChange={(v) => setTempPrefs(p => ({...p, show_recent_activity: v}))} data-testid="toggle-recent-activity" />
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-slate-200">
                <Button variant="outline" onClick={resetPreferences} disabled={saving} className="rounded-sm" data-testid="reset-dashboard-btn">
                  <ArrowCounterClockwise size={14} className="mr-1.5" /> Reset
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowCustomize(false)} className="rounded-sm">Cancel</Button>
                  <Button onClick={savePreferences} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm" data-testid="save-dashboard-btn">
                    {saving ? <SpinnerGap size={14} className="mr-1.5 animate-spin" /> : <Check size={14} className="mr-1.5" />}
                    Save
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
